// src/app/authentification/role-redirect.guard.ts
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { AuthService } from '../service/auth.service';
import { normalizeAppRoles } from '../core/role-utils';
import { hasPartnerRole, workspaceHomeForShell, workspaceShellForRoles } from '../core/workspace-role';
import { PrivilegedClaimsService } from '../core/privileged-claims.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RoleRedirectGuard implements CanActivate {
  constructor(
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly privilegedClaims: PrivilegedClaimsService
  ) {}

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    let roles = normalizeAppRoles(
      await firstValueFrom(this.auth.roles$.pipe(take(1)))
    );

    const privileged = await firstValueFrom(this.privilegedClaims.state$.pipe(take(1)));
    if (privileged.isSuperAdmin) roles = ['super_admin'];
    if (!roles.length) {
      if (privileged.isSuperAdmin) roles = ['super_admin'];
      else if (privileged.isOrgAdmin) roles = ['org_admin'];
      else return this.router.parseUrl('/login');
    }

    // Preserve specialized workspaces that intentionally sit outside the
    // four primary shells.
    let target: string;
    if (hasPartnerRole(roles)) target = '/partner/dashboard';
    else if (roles.includes('patient')) {
      target = ['jade-dev-5b0e3', 'woundapp-261e6'].includes(environment.firebase.projectId)
        ? '/telehealth-dev/patient-portal'
        : '/forbidden';
    }
    else if (roles.includes('don')) target = '/wound-rounds/dashboard';
    else if (roles.includes('wound_nurse')) target = '/wound-rounds';
    else if (roles.includes('surveyor')) target = '/surveyor/patients';
    else if (roles.includes('housekeeping')) target = '/housekeeping';
    else if (roles.includes('laundry')) target = '/laundry';
    else if (roles.includes('finance')) target = '/finance';
    else if (roles.includes('social-worker')) target = '/social';
    else if (roles.includes('lab')) target = '/provider/labs';
    else if (roles.includes('super_admin')) target = '/super-admin/dashboard';
    else if (roles.includes('org_admin')) target = '/admin/dashboard';
    else target = workspaceHomeForShell(workspaceShellForRoles(roles));

    if (this.isAlreadyUnderArea(state.url, target)) return true;
    return this.router.parseUrl(target);
  }

  private isAlreadyUnderArea(currentUrl: string, target: string): boolean {
    const currentRoot = '/' + currentUrl.split('/').filter(Boolean)[0];
    const targetRoot = '/' + target.split('/').filter(Boolean)[0];
    return currentRoot === targetRoot;
  }
}
